
import { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Package,
  Filter,
  Download,
  RefreshCw,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  Wrench,
  ClipboardCheck,
  Search,
  MapPin,
  User,
} from 'lucide-react';
import { inventoryMovementsAPI } from '../../services/garageApi';

// Movement type configuration — single source of truth
const MOVEMENT_CONFIG = {
  in: {
    label: 'Stock In',
    icon: ArrowUpCircle,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    badgeColor: 'bg-green-100 text-green-700',
    sign: '+',
    qtyColor: 'text-green-600',
  },
  out: {
    label: 'Stock Out',
    icon: ArrowDownCircle,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    badgeColor: 'bg-red-100 text-red-700',
    sign: '-',
    qtyColor: 'text-red-600',
  },
  transfer: {
    label: 'Transfer',
    icon: ArrowLeftRight,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    badgeColor: 'bg-blue-100 text-blue-700',
    sign: '',
    qtyColor: 'text-blue-600',
  },
  adjustment: {
    label: 'Adjustment',
    icon: Wrench,
    iconColor: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    badgeColor: 'bg-yellow-100 text-yellow-700',
    sign: '',
    qtyColor: 'text-yellow-600',
  },
  count: {
    label: 'Stock Count',
    icon: ClipboardCheck,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    badgeColor: 'bg-purple-100 text-purple-700',
    sign: '',
    qtyColor: 'text-purple-600',
  },
};

const PRODUCT_TYPE_COLORS = {
  battery: 'bg-blue-100 text-blue-800',
  tire: 'bg-green-100 text-green-800',
  wiper: 'bg-purple-100 text-purple-800',
};

/**
 * Parse the transfer reference JSON to get a human-readable direction.
 */
function parseTransferInfo(reference) {
  if (!reference) return null;
  try {
    const data = typeof reference === 'string' ? JSON.parse(reference) : reference;
    if (!data?.direction) return null;

    if (data.direction === 'out') {
      return {
        direction: 'out',
        label: `To ${data.target_location || 'Unknown'} — ${data.target_zone || 'Unknown'}`,
      };
    }
    return {
      direction: 'in',
      label: `From ${data.source_location || 'Unknown'} — ${data.source_zone || 'Unknown'}`,
    };
  } catch {
    return null;
  }
}

/**
 * Resolve display config for a movement, handling transfer direction.
 */
function getMovementDisplay(movement) {
  const type = movement.type;
  const config = MOVEMENT_CONFIG[type] || MOVEMENT_CONFIG.adjustment;

  // For transfers, detect direction from the reference JSON
  if (type === 'transfer') {
    const transferInfo = parseTransferInfo(movement.raw_reference || movement.reference);
    if (transferInfo) {
      const isIn = transferInfo.direction === 'in';
      return {
        ...config,
        label: isIn ? 'Transfer In' : 'Transfer Out',
        icon: isIn ? ArrowUpCircle : ArrowDownCircle,
        iconColor: isIn ? 'text-green-600' : 'text-red-600',
        badgeColor: isIn ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
        sign: isIn ? '+' : '-',
        qtyColor: isIn ? 'text-green-600' : 'text-red-600',
        transferLabel: transferInfo.label,
      };
    }
  }

  // For adjustments, use quantity sign to determine direction
  if (type === 'adjustment') {
    const qty = parseInt(movement.quantity) || 0;
    const isPositive = qty > 0;
    return {
      ...config,
      sign: isPositive ? '+' : '',
      qtyColor: isPositive ? 'text-green-600' : 'text-red-600',
    };
  }

  return config;
}

const InventoryMovements = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    product_type: '',
    date_from: '',
    date_to: '',
    search: '',
  });
  const [stats, setStats] = useState({
    totalIn: 0,
    totalOut: 0,
    netMovement: 0,
    recentCount: 0,
  });

  const fetchMovements = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.product_type) params.product_type = filters.product_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.search) params.search = filters.search;

      const response = await inventoryMovementsAPI.getAll(params);

      let movementsData = [];
      if (response.data && Array.isArray(response.data)) {
        movementsData = response.data;
      } else if (Array.isArray(response)) {
        movementsData = response;
      }

      // Calculate statistics across all types
      let totalIn = 0;
      let totalOut = 0;

      movementsData.forEach((m) => {
        const qty = Math.abs(parseInt(m.quantity) || 0);
        const type = m.type;

        if (type === 'in') {
          totalIn += qty;
        } else if (type === 'out') {
          totalOut += qty;
        } else if (type === 'transfer') {
          const transferInfo = parseTransferInfo(m.raw_reference || m.reference);
          if (transferInfo?.direction === 'in') {
            totalIn += qty;
          } else {
            totalOut += qty;
          }
        } else if (type === 'adjustment') {
          const rawQty = parseInt(m.quantity) || 0;
          if (rawQty > 0) totalIn += Math.abs(rawQty);
          else if (rawQty < 0) totalOut += Math.abs(rawQty);
        }
      });

      setStats({
        totalIn,
        totalOut,
        netMovement: totalIn - totalOut,
        recentCount: movementsData.length,
      });

      setMovements(movementsData);
    } catch (err) {
      console.error('Error fetching inventory movements:', err);
      setError('Failed to load inventory movements: ' + err.message);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ type: '', product_type: '', date_from: '', date_to: '', search: '' });
  };

  const exportMovements = () => {
    const headers = ['Date', 'Type', 'Product Type', 'Product', 'Qty', 'Before', 'After', 'User', 'Location', 'Zone', 'Reason', 'Reference'];
    const csvContent = [
      headers.join(','),
      ...movements.map((m) =>
        [
          new Date(m.created_at).toLocaleDateString(),
          m.type,
          m.product_type || '',
          m.product_name || '',
          m.quantity,
          m.quantity_before ?? '',
          m.quantity_after ?? '',
          m.user_name || '',
          m.location_name || '',
          m.zone_name || '',
          m.notes || '',
          typeof m.reference === 'string' ? m.reference : '',
        ]
          .map((f) => `"${f}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-movements-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        <span className="ml-3 text-gray-500">Loading movements...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Movements</h1>
          <p className="text-gray-600">Track all stock changes across your inventory</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportMovements}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            onClick={fetchMovements}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <ArrowUpCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total In</p>
                <p className="text-lg font-semibold text-gray-900">{stats.totalIn.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <ArrowDownCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Out</p>
                <p className="text-lg font-semibold text-gray-900">{stats.totalOut.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <TrendingUp className={`h-6 w-6 flex-shrink-0 ${stats.netMovement >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Net Movement</p>
                <p className={`text-lg font-semibold ${stats.netMovement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.netMovement >= 0 ? '+' : ''}{stats.netMovement.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <Package className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Records</p>
                <p className="text-lg font-semibold text-gray-900">{stats.recentCount.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700">
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search..."
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Movement Type</label>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">All Types</option>
              <option value="in">Stock In</option>
              <option value="out">Stock Out</option>
              <option value="transfer">Transfer</option>
              <option value="adjustment">Adjustment</option>
              <option value="count">Stock Count</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
            <select
              name="product_type"
              value={filters.product_type}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">All Products</option>
              <option value="tire">Tires</option>
              <option value="battery">Batteries</option>
              <option value="wiper">Wipers</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchMovements}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Movements Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Movement History</h3>
        </div>

        {movements.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <Package className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p>No inventory movements found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Before / After</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {movements.map((movement) => {
                  const display = getMovementDisplay(movement);
                  const Icon = display.icon;
                  const qty = parseInt(movement.quantity) || 0;

                  return (
                    <tr key={movement.id} className="hover:bg-gray-50 transition-colors">
                      {/* Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${display.bgColor}`}>
                            <Icon className={`h-4 w-4 ${display.iconColor}`} />
                          </div>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${display.badgeColor}`}>
                            {display.label}
                          </span>
                        </div>
                      </td>

                      {/* Product */}
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {movement.product_name || 'Unknown'}
                          </p>
                          {movement.product_type && (
                            <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${PRODUCT_TYPE_COLORS[movement.product_type] || 'bg-gray-100 text-gray-700'}`}>
                              {movement.product_type}
                            </span>
                          )}
                          {movement.location_name && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              <span className="text-xs text-gray-400">
                                {movement.location_name}{movement.zone_name ? ` (${movement.zone_name})` : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Quantity */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${display.qtyColor}`}>
                          {display.sign}{Math.abs(qty)}
                        </span>
                      </td>

                      {/* Before / After */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {movement.quantity_before != null && movement.quantity_after != null ? (
                          <span className="text-xs text-gray-500">
                            {movement.quantity_before} &rarr; {movement.quantity_after}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">&mdash;</span>
                        )}
                      </td>

                      {/* Details: reason + transfer info */}
                      <td className="px-6 py-4 max-w-xs">
                        {display.transferLabel ? (
                          <div className="flex items-start gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-600">{display.transferLabel}</p>
                          </div>
                        ) : movement.notes || movement.reason ? (
                          <p className="text-xs text-gray-600 truncate">{movement.notes || movement.reason}</p>
                        ) : (
                          <span className="text-xs text-gray-300">&mdash;</span>
                        )}
                        {movement.notes && display.transferLabel && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{movement.notes}</p>
                        )}
                      </td>

                      {/* User */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {movement.user_name ? (
                          <div className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-xs text-gray-600">{movement.user_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">System</span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <p className="text-xs text-gray-500">
                          {new Date(movement.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(movement.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryMovements;
